import React from 'react';
import { TFunction } from 'i18next';
import { ReactComponent as AddSearchIcon } from '../../../../assets/svg/Schedule/add&search.svg';
import { ReactComponent as ArrowFrontIcon } from '../../../../assets/svg/Schedule/arrow-front.svg';
import { ReactComponent as ArrowAfterIcon } from '../../../../assets/svg/Schedule/arrow-after.svg';
import { ReactComponent as NoteIcon } from '../../../../assets/svg/Schedule/note.svg';
import { ReactComponent as SplitIcon } from '../../../../assets/svg/Schedule/split.svg';
import { ReactComponent as MergeIcon } from '../../../../assets/svg/Schedule/merge.svg';
import { ReactComponent as ReturnDayIcon } from '../../../../assets/svg/Schedule/return-day.svg';

export interface ScheduleHeaderBarProps {
  canEdit: boolean;
  scheduleID: string | undefined;
  scheduleName: string;
  addButtonRef: React.RefObject<HTMLButtonElement>;
  onOpenScheduleSearch: () => void;
  onNameChange: React.ChangeEventHandler<HTMLInputElement>;
  onNameBlur: React.FocusEventHandler<HTMLInputElement>;
  onNameKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  currentDate: Date | null;
  t: TFunction;
  onReturnToCurrentDate: () => void;
  onAddNote: () => void;
  /** true=显示所有地点（UI 忽略 slotLocationVisibility） */
  showAllLocations: boolean;
  /** 切换“显示所有地点/按地点整理” */
  onShowAllLocationsChange: (showAll: boolean) => void;
  onInsertDate: () => void;
  deleteDateButtonRef: React.RefObject<HTMLButtonElement>;
  onDeleteDateClick: () => void;
  onPreviousPeriod: () => void;
  onNextPeriod: () => void;
  dateSelectBtnRef: React.RefObject<HTMLButtonElement>;
  onOpenDateSelect: () => void;
  getDateDisplayText: () => string;
}

export const ScheduleHeaderBar: React.FC<ScheduleHeaderBarProps> = ({
  canEdit,
  scheduleID,
  scheduleName,
  addButtonRef,
  onOpenScheduleSearch,
  onNameChange,
  onNameBlur,
  onNameKeyDown,
  currentDate,
  t,
  onReturnToCurrentDate,
  onAddNote,
  showAllLocations,
  onShowAllLocationsChange,
  onInsertDate,
  deleteDateButtonRef,
  onDeleteDateClick,
  onPreviousPeriod,
  onNextPeriod,
  dateSelectBtnRef,
  onOpenDateSelect,
  getDateDisplayText,
}) => (
  <div className="schedule-header">
    <div className="schedule-header-left">
      <button ref={addButtonRef} className="schedule-add-button-btn" onClick={onOpenScheduleSearch}>
        <AddSearchIcon className="schedule-add-button-icon" aria-label="添加" />
      </button>
      <div className="schedule-name">
        <input
          type="text"
          placeholder={scheduleID ? t('schedule.untitled') : t('schedule.selectOrCreateScheduleHint')}
          value={scheduleName}
          onChange={scheduleID ? onNameChange : undefined}
          onBlur={scheduleID ? onNameBlur : undefined}
          onKeyDown={scheduleID ? onNameKeyDown : undefined}
          readOnly={!scheduleID || !canEdit}
          className={`schedule-name-input${!scheduleID ? ' schedule-name-input--no-schedule' : ''}`}
        />
      </div>
    </div>

    {scheduleID && currentDate && (
      <div className="schedule-header-actions">
        <button
          className="scheduleDetails-date-action-btn scheduleDetails-date-action-btn-return"
          onClick={onReturnToCurrentDate}
          title={t('schedule.returnToCurrentDate')}
        >
          <ReturnDayIcon className="scheduleDetails-date-action-icon" />
          {t('schedule.returnToCurrentDate')}
        </button>
        {canEdit && <button
          className="scheduleDetails-date-action-btn scheduleDetails-date-action-btn-addNote"
          onClick={onAddNote}
          title={t('schedule.addNote')}
        >
          <NoteIcon className="scheduleDetails-date-action-icon" />
          {t('schedule.addNote')}
        </button>}
        {canEdit && <label
          className={
            'scheduleDetails-location-toggle' +
            (showAllLocations
              ? ' scheduleDetails-location-toggle--left'
              : ' scheduleDetails-location-toggle--right')
          }
          title={showAllLocations ? t('schedule.showAllLocations') : t('schedule.organizeByLocation')}
        >
          <input
            type="checkbox"
            role="switch"
            aria-label={t('schedule.showAllLocations') + ' / ' + t('schedule.organizeByLocation')}
            checked={showAllLocations}
            onChange={(e) => onShowAllLocationsChange(e.target.checked)}
          />
          <span className="scheduleDetails-location-toggle-track" aria-hidden="true">
            <span className="scheduleDetails-location-toggle-indicator" />
            <span className="scheduleDetails-location-toggle-option scheduleDetails-location-toggle-option--left">
              {t('schedule.showAllLocations')}
            </span>
            <span className="scheduleDetails-location-toggle-option scheduleDetails-location-toggle-option--right">
              {t('schedule.organizeByLocation')}
            </span>
          </span>
        </label>}
        {canEdit && <button
          className="scheduleDetails-date-action-btn scheduleDetails-date-action-btn-insert"
          onClick={onInsertDate}
          title={t('schedule.insertBlankDay')}
        >
          <SplitIcon className="scheduleDetails-date-action-icon" />
          {t('schedule.insertBlankDay')}
        </button>}
        {canEdit && <button
          ref={deleteDateButtonRef}
          className="scheduleDetails-date-action-btn scheduleDetails-date-action-btn-delete"
          onClick={onDeleteDateClick}
          title={t('schedule.deleteThisDay')}
        >
          <MergeIcon className="scheduleDetails-date-action-icon" />
          {t('schedule.deleteThisDay')}
        </button>}
      </div>
    )}

    <div className="schedule-header-right">
      <button className="schedule-date-front" onClick={onPreviousPeriod} title="上一月">
        <ArrowFrontIcon className="schedule-date-icon" aria-label="上一月" />
      </button>

      <button ref={dateSelectBtnRef} className="schedule-date-select-btn" onClick={onOpenDateSelect}>
        {getDateDisplayText()}
      </button>

      <button className="schedule-date-after" onClick={onNextPeriod} title="下一月">
        <ArrowAfterIcon className="schedule-date-icon" aria-label="下一月" />
      </button>
    </div>
  </div>
);
